import { forwardRef } from 'react'
import { cn } from '../../utils/cn'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Button - Stamp/Washi Tape Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

// Helper to filter out background/color classes when in diary mode
const filterDiaryClasses = (className: string | undefined, isDiary: boolean): string => {
  if (!isDiary || !className) return className || ''
  
  // Remove bg-*, from-*, to-*, text-* (color) classes in diary mode
  return className
    .split(' ')
    .filter(cls => {
      if (cls.startsWith('bg-')) return false
      if (cls.startsWith('from-')) return false
      if (cls.startsWith('to-')) return false
      if (cls.startsWith('via-')) return false
      if (cls.match(/^text-(gray|blue|green|red|purple|pink|orange|yellow|indigo|emerald|rose|slate|white|black)/)) return false
      if (cls.startsWith('border-')) return false
      if (/dark:(bg|text|border|ring|from|to|via|divide|placeholder)-/.test(cls)) return false
      if (cls.includes('hover:bg-')) return false
      if (cls.includes('hover:from-')) return false
      if (cls.includes('hover:to-')) return false
      return true
    })
    .join(' ')
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'xs' | 'lg' | 'icon'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', style, ...props }, ref) => {
    const { isDiary, yanamasButtonStyle } = useTheme()
    
    // Diary theme styles. The PRIMARY variant's look is a user choice
    // (yanamasButtonStyle, default 'sketch' per the theme-lab picks); the
    // other variants keep one consistent voice each.
    const getDiaryStyles = (): React.CSSProperties | undefined => {
      if (!isDiary) return undefined
      
      const baseStyles: React.CSSProperties = {
        // Track the theme's UI font var, not a hardcoded family, so the
        // customizer's font pick reaches buttons too.
        fontFamily: "var(--font-ui, 'Quicksand', sans-serif)",
        transition: 'all 0.2s ease',
      }
      
      switch (variant) {
        case 'default':
          switch (yanamasButtonStyle) {
            case 'sketch':
              // Hand-drawn box: wonky pen border + handwriting (C-BTN 3)
              return {
                ...baseStyles,
                backgroundColor: 'transparent',
                color: 'var(--ink-black)',
                border: '2px solid var(--ink-black)',
                borderRadius: '14px 5px 16px 6px',
                fontFamily: "var(--font-handwritten, 'Gochi Hand', cursive)",
                fontSize: '1.05em',
                boxShadow: 'none',
              }
            case 'ink':
              return {
                ...baseStyles,
                backgroundColor: 'var(--ink-sepia)',
                color: 'var(--paper-cream)',
                border: 'none',
                borderRadius: '4px',
                boxShadow: '2px 2px 6px var(--shadow-color, rgba(92, 64, 51, 0.2))',
              }
            case 'tape':
              return {
                ...baseStyles,
                backgroundColor: 'var(--washi-tape-pink)',
                color: 'var(--ink-sepia)',
                border: 'none',
                borderRadius: '2px',
                transform: 'rotate(-1.5deg)',
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.5) 4px, rgba(255,255,255,0.5) 8px)',
                boxShadow: '2px 2px 6px var(--shadow-color, rgba(92, 64, 51, 0.15))',
              }
            case 'wax':
            default:
              // Wax seal / stamp aesthetic - BOLD PURPLE
              return {
                ...baseStyles,
                backgroundColor: 'var(--horton-purple)', // brand purple - explicit
                color: 'var(--paper-cream)', // Cream text
                border: 'none',
                boxShadow: '3px 3px 8px rgba(123, 104, 166, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                borderRadius: '6px',
                fontWeight: 600,
              }
          }
        case 'secondary':
          // Washi tape strip - PINK with pattern
          return {
            ...baseStyles,
            backgroundColor: 'var(--washi-tape-pink)', // Pink washi tape
            color: 'var(--ink-sepia)', // Sepia text
            border: 'none',
            boxShadow: '2px 2px 6px rgba(92, 64, 51, 0.15)',
            borderRadius: '3px',
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.5) 4px, rgba(255,255,255,0.5) 8px)',
          }
        case 'outline':
          // Pen-drawn border - VISIBLE dashed
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: 'var(--ink-sepia)',
            border: '2px dashed var(--ink-faded)', // Dashed border like pen
            borderRadius: '4px',
            boxShadow: 'none',
          }
        case 'ghost':
          // Subtle hover like paper lift
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: 'var(--ink-black)',
            border: 'none',
          }
        case 'destructive':
          // Red ink / correction - BOLD RED
          return {
            ...baseStyles,
            backgroundColor: 'var(--bookmark-red)', // Bookmark red
            color: 'var(--paper-cream)',
            border: 'none',
            boxShadow: '3px 3px 8px rgba(196, 92, 74, 0.4)',
          }
        case 'link':
          // Underlined handwritten link
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: 'var(--ink-blue)', // Ink blue
            textDecoration: 'underline',
            textDecorationStyle: 'wavy',
            textUnderlineOffset: '3px',
          }
        default:
          return baseStyles
      }
    }
    
    // Hover styles for diary theme
    const getDiaryHoverClass = (): string => {
      if (!isDiary) return ''
      
      switch (variant) {
        case 'default':
          return 'hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0'
        case 'secondary':
          return 'hover:brightness-105 hover:rotate-1 active:rotate-0'
        case 'outline':
          return 'hover:bg-[var(--paper-aged)] hover:border-[var(--ink-sepia)]'
        case 'ghost':
          return 'hover:bg-[rgba(232,224,213,0.5)]'
        case 'destructive':
          return 'hover:brightness-110 hover:-translate-y-0.5'
        case 'link':
          return 'hover:text-[var(--bookmark-red)]'
        default:
          return ''
      }
    }
    
    // Filter className in diary mode to remove conflicting background/color classes
    const filteredClassName = filterDiaryClasses(className, isDiary)
    
    return (
      <button
        className={cn(
          // 2026-08-29 UI audit: the focus ring had no colour and no offset
          // colour — `ring-offset-background` is a shadcn-era class that emits
          // ZERO css, and a bare ring-2 falls back to currentColor over a white
          // offset, so keyboard focus was invisible in the default theme and a
          // white halo on the dark ones. Both are token-driven now.
          'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-50',
          !isDiary && 'focus-visible:ring-brand',
          !isDiary && {
            'bg-brand text-on-brand hover:bg-brand-hover': variant === 'default',
            'bg-surface-alt text-primary border border-brand-subtle hover:bg-brand-subtle': variant === 'secondary',
            'border border-brand-subtle bg-surface text-brand hover:bg-brand-subtle': variant === 'outline',
            'text-primary hover:bg-brand-subtle': variant === 'ghost',
            'bg-bad text-on-brand hover:brightness-110': variant === 'destructive',
            'text-brand underline-offset-4 hover:underline': variant === 'link',
          },
          isDiary && 'focus-visible:ring-[var(--horton-purple)] diary-ink-ripple', // Added ink ripple effect
          getDiaryHoverClass(),
          {
            'h-11 px-4 py-2': size === 'default',
            // `sm` is a tighter button, not a smaller TARGET. It was h-10 (40px)
            // at ~200 call sites, under the 44px floor (2026-08-29 UI audit),
            // so the height floor is the shared token and only the padding
            // still distinguishes it from `default`.
            'min-h-touch rounded-md px-3 py-2': size === 'sm',
            // `xs` keeps the 44px floor where fingers are (below lg) and drops
            // to 36px on a laptop, where a row of 44px buttons read as bigger
            // than everything around them (owner 2026-09-05, task detail).
            'min-h-touch lg:min-h-0 lg:h-9 rounded-md px-3 py-1.5 text-sm': size === 'xs',
            'h-12 rounded-md px-8': size === 'lg',
            'h-11 w-11': size === 'icon',
          },
          filteredClassName
        )}
        style={isDiary ? { ...getDiaryStyles(), ...style } : style}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button }